// PROXY's ACE workflow registry — the single map from a task type to the
// workflow module that owns everything about it: what triggers a task, where
// its data comes from, what its item-detail review panel shows, and what
// fires on approve (see data/proxyWorkflowTypes.ts for the full contract).
//
// The Intake Queue and the item-detail panel (components/proxy/ProxyTaskPanel)
// render ONLY off this registry and the generic ProxyTask/ProxyReviewPanelSchema
// shapes — neither imports a workflow module directly. Adding a new task type
// is: write data/workflows/<name>.ts implementing ProxyWorkflowDefinition, add
// one line below. No new page, route, or panel component.
//
// RDQ_ORDER and SHORTAGE_FOLLOWUP are registered today. Follow-Up / Contest /
// Inbox-triage workflows are deliberately not built yet.
import type { ProxyJob } from './mockProxyJobs';
import type { ProxyInboxEmail } from './mockProxyEmails';
import type { ProxyWorkflowType, ProxyWorkflowContext, ProxyWorkflowDefinition, ProxyTask } from './proxyWorkflowTypes';
import { RDQ_ORDER_WORKFLOW, buildRdqOrderTask } from './workflows/rdqOrderWorkflow';
import { SHORTAGE_FOLLOWUP_WORKFLOW, shortageResolved } from './workflows/shortageFollowupWorkflow';
import { MATERIAL_FOLLOWUP_WORKFLOW, buildMaterialFollowupTask, materialFollowupResolved } from './workflows/materialFollowupWorkflow';
import { PROXY_CONTEST_WORKFLOW } from './workflows/proxyContestWorkflow';
import {
  MATERIAL_PDF_ROUTE_WORKFLOW, JOB_CHANGE_ROUTE_WORKFLOW,
  BILLING_VERIFICATION_ROUTE_WORKFLOW, CREDIT_HOLD_ROUTE_WORKFLOW,
} from './workflows/emailRouteWorkflow';
import { UNMATCHED_MATERIAL_WORKFLOW } from './workflows/unmatchedMaterialWorkflow';
import { EMAIL_BOUNCE_ROUTE_WORKFLOW } from './workflows/emailBounceWorkflow';
import { MISSING_CONTACT_ROUTE_WORKFLOW } from './workflows/missingContactWorkflow';
import { FOLLOW_UP_REPLY_WORKFLOW } from './workflows/followUpReplyWorkflow';
import { SHORTAGE_CLOSE_WORKFLOW } from './workflows/shortageCloseWorkflow';

export type {
  ProxyWorkflowType, ProxyTaskSource, ProxyTask, ProxyTaskDetailRow, ProxyWorkflowContext, ProxyWorkflowDefinition,
  ProxyFieldOption, ProxyExtractedField, ProxyCalcRow, ProxyCalcSchema, ProxyDraftSchema,
  ProxyOutboundAction, ProxyReviewPanelSchema, ProxyPanelContext,
} from './proxyWorkflowTypes';
export { PROXY_SOURCE_LABEL } from './proxyWorkflowTypes';
// RDQ_ORDER's single-job task builder is re-exported for the RDQ-specific Ace
// chat shortcuts in utils/proxyAceHandlers.ts — a feature of that workflow,
// not part of the generic registry contract.
export { buildRdqOrderTask };
// MATERIAL_FOLLOWUP's single-job task builder + resolved-check are re-exported
// for the Reports page's "Create follow-up" action — same non-generic-contract
// carve-out as buildRdqOrderTask above.
export { buildMaterialFollowupTask, materialFollowupResolved };
// Ditto for SHORTAGE_FOLLOWUP's resolved-check — reused by Ace's Proxy
// landing-state "shortages awaiting response" card (pages/AcePage.tsx).
export { shortageResolved };

export const PROXY_WORKFLOW_REGISTRY: Record<ProxyWorkflowType, ProxyWorkflowDefinition> = {
  RDQ_ORDER: RDQ_ORDER_WORKFLOW,
  SHORTAGE_FOLLOWUP: SHORTAGE_FOLLOWUP_WORKFLOW,
  MATERIAL_FOLLOWUP: MATERIAL_FOLLOWUP_WORKFLOW,
  PROXY_CONTEST: PROXY_CONTEST_WORKFLOW,
  MATERIAL_PDF_ROUTE: MATERIAL_PDF_ROUTE_WORKFLOW,
  JOB_CHANGE_ROUTE: JOB_CHANGE_ROUTE_WORKFLOW,
  BILLING_VERIFICATION_ROUTE: BILLING_VERIFICATION_ROUTE_WORKFLOW,
  CREDIT_HOLD_ROUTE: CREDIT_HOLD_ROUTE_WORKFLOW,
  UNMATCHED_MATERIAL_ROUTE: UNMATCHED_MATERIAL_WORKFLOW,
  EMAIL_BOUNCE_ROUTE: EMAIL_BOUNCE_ROUTE_WORKFLOW,
  MISSING_CONTACT_ROUTE: MISSING_CONTACT_ROUTE_WORKFLOW,
  FOLLOW_UP_REPLY: FOLLOW_UP_REPLY_WORKFLOW,
  SHORTAGE_CLOSE: SHORTAGE_CLOSE_WORKFLOW,
};

export function buildProxyIntakeTasks(ctx: ProxyWorkflowContext): ProxyTask[] {
  return Object.values(PROXY_WORKFLOW_REGISTRY).flatMap(wf => wf.buildTasks(ctx));
}

// A ticket is one case: the steps (tasks) of one piece of work. A shortage,
// a material chase or a contest is one ticket per job, however many tasks
// it takes; one-off work (routing an email, an RDQ order, checking a
// contact, unmatched material) is a ticket per task.
export type TicketFamily = 'shortage' | 'material' | 'contest' | 'rdq' | 'routing' | 'contact' | 'unmatched';

export const TICKET_LABEL: Record<TicketFamily, string> = {
  shortage: 'Material shortage', material: 'Material follow-up', contest: 'Proxy contest',
  rdq: 'RDQ order', routing: 'Email routing', contact: 'Contact check', unmatched: 'Unmatched material',
};

export function ticketFamily(type: ProxyWorkflowType, job?: ProxyJob): TicketFamily {
  switch (type) {
    case 'SHORTAGE_FOLLOWUP': case 'SHORTAGE_CLOSE': return 'shortage';
    case 'FOLLOW_UP_REPLY': return job?.shortageLines?.length ? 'shortage' : 'material';
    case 'MATERIAL_FOLLOWUP': return 'material';
    case 'PROXY_CONTEST': return 'contest';
    case 'RDQ_ORDER': return 'rdq';
    case 'EMAIL_BOUNCE_ROUTE': case 'MISSING_CONTACT_ROUTE': return 'contact';
    case 'UNMATCHED_MATERIAL_ROUTE': return 'unmatched';
    default: return 'routing';
  }
}

const PER_JOB: TicketFamily[] = ['shortage', 'material', 'contest'];

export function ticketKey(task: Pick<ProxyTask, 'id' | 'workflowType' | 'jobId'>, job?: ProxyJob): string {
  const family = ticketFamily(task.workflowType, job);
  return PER_JOB.includes(family) ? `${family}:${task.jobId}` : `${family}:${task.id}`;
}

// The open material follow-up covering a job, if Ace already prepared one
// (possibly as part of a solicitor's grouped email).
export function materialFollowupTaskFor(tasks: ProxyTask[], jobId: string): ProxyTask | undefined {
  return tasks.find(t => t.workflowType === 'MATERIAL_FOLLOWUP' && (t.jobIds ?? [t.jobId]).includes(jobId));
}

// ── Bucket predicates — shared by the Intake Queue's Exceptions/Escalations/
// Follow-Ups pages and the Sidebar badge counts, so a task's bucket
// membership is defined in exactly one place. A task can be both an
// Exception and Escalated at once (independent axes, same as Fulfillment's
// material_status vs escalated_to_team) — Follow-Ups is deliberately the
// "neither" bucket, the normal/confident/contactable queue.
//
// An exception is a task that IS itself the problem — not just any task that
// happens to belong to a contact-less job. RDQ orders, contests, billing/
// credit-hold/PDF routing, and material follow-ups all handle a missing
// solicitor gracefully on their own (see proxyFallbackSolicitors.ts) and stay
// in Follow-Ups; MISSING_CONTACT_ROUTE is the one dedicated task per
// contact-less job that surfaces it here instead.
const EXCEPTION_WORKFLOW_TYPES = new Set<ProxyTask['workflowType']>([
  'MISSING_CONTACT_ROUTE', 'EMAIL_BOUNCE_ROUTE', 'UNMATCHED_MATERIAL_ROUTE',
]);

export function isProxyTaskException(task: ProxyTask, job: ProxyJob): boolean {
  return EXCEPTION_WORKFLOW_TYPES.has(task.workflowType);
}

// Escalations: tasks that need the ICT manager. Either a person escalated
// the job (with a reason), or the SOP makes this decision the manager's and
// Ace sent it up itself (see ProxyReviewPanelSchema.escalation).
export interface ProxyEscalation {
  by: 'Ace' | 'You';
  reason: string;
}

export function escalationFor(task: ProxyTask, job: ProxyJob, emails: ProxyInboxEmail[]): ProxyEscalation | null {
  // A person's escalation: the reason is their comment, which is optional.
  if (job.escalatedToTeam) return { by: 'You', reason: job.escalationReason || '' };
  const wf = PROXY_WORKFLOW_REGISTRY[task.workflowType];
  const email = task.sourceEmailId ? emails.find(e => e.id === task.sourceEmailId) : undefined;
  const reason = wf.buildPanel({ task, job, edits: wf.defaultEdits(task, job), email }).escalation;
  return reason ? { by: 'Ace', reason } : null;
}

export interface ProxyIntakeSummary {
  emailsRead: number;
  tasksCreated: number;
  autoCleared: number;
  needReview: number;
}

// Powers the Intake Queue's "Ace read N emails → M tasks…" header line.
export function buildProxyIntakeSummary(ctx: ProxyWorkflowContext): ProxyIntakeSummary {
  const tasks = buildProxyIntakeTasks(ctx);
  const needReview = tasks.filter(t => {
    const job = ctx.jobs.find(j => j.jobId === t.jobId);
    return job && isProxyTaskException(t, job);
  }).length;
  return {
    // Deduped by id, not ctx.emails.length — the Follow-Up Report flattens
    // into one row per job it lists (so RDQ_ORDER can match each by
    // relatedJobId), but it's still one email Ace read, not N.
    emailsRead: new Set(ctx.emails.map(e => e.id)).size,
    tasksCreated: tasks.length,
    autoCleared: tasks.length - needReview,
    needReview,
  };
}
